/* Copyright 2018 - 2020 Ellucian Company L.P. and its affiliates.
 * File: LogInView.tsx
 * Type: Container component */

// #region Imports
import React from 'react';
import ReactDOM from 'react-dom';

// Core components
import Layout from '@hedtech/powercampus-design-system/react/components/Layout'; // lCen ref banc
import Grid from '@hedtech/powercampus-design-system/react/core/Grid';

// Internal components
import SignIn from '../../Generic/SignIn';

// Types
import { ILogData } from '@hedtech/powercampus-design-system/types/ILogData';
import { ILogInResources } from '../../../Types/Resources/Home/ILogInResources';

// Helpers
import LogData from '@hedtech/powercampus-design-system/helpers/LogData';

// State Management
import LayoutActions from '@hedtech/powercampus-design-system/flux/actions/LayoutActions';
import LayoutStore from '@hedtech/powercampus-design-system/flux/stores/LayoutStore';
import { createStyles, withStyles, WithStyles } from '@hedtech/powercampus-design-system/react/core/styles/withStyles'; // lCen ref banc
// #endregion Imports

// #region Types
interface ILogInState {
    isLoading: boolean;
    resources?: ILogInResources;
}
// #endregion Types

// #region lCen ref banc
const styles = createStyles({
    footer: {
        marginTop: '1rem',
        backgroundColor: '#fff',
        position: 'fixed',
        bottom: 0, left: 0,
        width: '100%'
    },
    content: {
        padding: '0.5rem',
        display: 'flex'
    },
    left: {
        flexGrow: 1,
        color: '#000',
        textAlign: 'left',
        fontSize: '0.75rem',
        fontWeight: 400,
        fontFamily: '\'Open Sans\', Helvetica, Arial, sans-serif'
    },
    right: {
        flexGrow: 1,
        color: '#000',
        textAlign: 'right',
        fontSize: '0.75rem',
        fontWeight: 400,
        fontFamily: '\'Open Sans\', Helvetica, Arial, sans-serif'
    },
    line: {
        textDecoration: 'none',
        color: '#000',
        fontWeight: 'bold',
        fontSize: '0.75rem',
        fontFamily: '\'Open Sans\', Helvetica, Arial, sans-serif'
    },
    custom: {
        textAlign: 'center',
        color: '#5B5E65',
        fontFamily: 'Noto Sans',
        fontWeight: 400,
        lineHeight: 1.5,
        wordSpacing: '0.16em',
        width: '100%'
    }
});

type PropsWithStyles = WithStyles<typeof styles>;
// region lCen ref banc


// #region Component
class LogInView extends React.Component<PropsWithStyles, ILogInState> {
    private idModule: string;
    private idPage: string;

    public readonly state: Readonly<ILogInState>;

    public constructor(props: any) {
        super(props);

        // #region Initialize Variables and State
        this.idModule = 'Home';
        this.idPage = 'LogIn';
        this.state = this.getInitialState();
        // #endregion Initialize Variables and State

        // #region Bind State Management Listeners
        LayoutStore.addLayoutReadyListener(this.onLayoutReady);
        // #endregion State Management Listeners
    }

    private getInitialState(): ILogInState {
        let isLoading: boolean = true;
        let resources: ILogInResources | undefined;
        if (this.state) {
            isLoading = this.state.isLoading;
            resources = this.state.resources;
        }
        return {
            isLoading: isLoading,
            resources: resources
        };
    }

    // #region Loader Functions
    private hideAllLoaders = (): void => {
        this.setState({
            isLoading: false
        });
    };
    // #endregion Loader Functions

    // #region Error Functions
    private logError(logData: ILogData): void {
        this.hideAllLoaders();
        LayoutActions.setLogData(logData);
    }

    private redirectError(code: number): void {
        this.hideAllLoaders();
        LayoutActions.setRedirectCode(code);
    }
    // #endregion Error Functions

    // #region Resolvers
    private resolveLayoutReady = (): void => {
        try {
            const {
                resources
            } = this.state;

            if (resources) {
                document.title = resources.lblPageTitle;

                this.setState({
                    isLoading: false
                }, LayoutActions.hidePageLoader);
            }
            else {
                this.logError(LogData.noResources(this.resolveLayoutReady.name));
            }
        }
        catch (e) {
            this.logError(LogData.fromException(this.resolveLayoutReady.name, e));
        }
    };
    // #endregion Resolvers

    // #region State Management Events
    private onLayoutReady = (): void => {
        const ready: boolean = LayoutStore.getLayoutReady();
        const resources: ILogInResources | undefined = LayoutStore.getResources();

        if (ready) {
            this.setState({
                resources: resources
            }, this.resolveLayoutReady);
        }
        else {
            this.logError(LogData.layoutNoReady(this.onLayoutReady.name));
            this.redirectError(500);
        }
    };
    // #endregion State Management Events

    // #region Lifecycle
    public componentWillUnmount(): void {
        LayoutStore.removeLayoutReadyListener(this.onLayoutReady);
    }
    // #endregion Lifecycle

    public render(): JSX.Element {
        const {
            isLoading,
            resources
        } = this.state;

        // #region lCen ref banc
        const {
            classes
        } = this.props;

        // #endregion lCen ref banc

        let contentPage: JSX.Element | JSX.Element[] | undefined;

        if (resources && !isLoading) {
            contentPage = (
                <Grid container justify="center">
                    <Grid item xs={12} sm={10} md={8} lg={4}>
                        <SignIn />
                    </Grid>
                </Grid>
            );
        }

        let contentPageFooter: JSX.Element | JSX.Element[] | undefined;
        if (resources && !isLoading) {

            contentPageFooter = (
                <div className={classes.footer}>
                    <div className={classes.content}>
                        <div className={classes.custom}>
                            {/* <p>{resources.footer.formatTextInfo}*/}
                            <p>
                                {resources.footer.formatText}
                                <a href={resources.footer.customUrlRedirect} target="_blank">
                                    {resources.footer.customUrlRedirect}
                                </a>

                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <Layout
                idModule={this.idModule}
                idPage={this.idPage}
                showCart
                showNotifications
                withBackgroundImage
            // *withFooter* //lCen ref banc
            >
                {contentPage}
                {contentPageFooter}
            </Layout>
        );
    }
}

const LogInViewWithLayout = withStyles(styles)(LogInView); // lCen ref banc

// #endregion Component

// RenderDOM: Component
ReactDOM.render(<LogInViewWithLayout />, document.getElementById('root'));